import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    function handleScroll() {
      const nav = document.getElementById('main-nav');
      if (nav) {
        if (window.scrollY > 50) {
          nav.classList.add('bg-background-50/95', 'backdrop-blur-md');
          nav.classList.remove('bg-transparent');
        } else {
          nav.classList.add('bg-transparent');
          nav.classList.remove('bg-background-50/95', 'backdrop-blur-md');
        }
      }
    }
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const categoryIcons: Record<string, string> = {
    "Shoes": "ri-footprint-line",
    "Hoodies / Sweaters": "ri-t-shirt-line",
    "T-Shirts": "ri-t-shirt-line",
    "Jackets": "ri-shirt-line",
    "Pants / Shorts": "ri-t-shirt-line",
    "Headwear": "ri-user-3-line",
    "Sets": "ri-stack-line",
    "Underwear / Underpants": "ri-shirt-line",
    "Jersey": "ri-t-shirt-air-line",
    "Accessories": "ri-vip-crown-line",
    "Bags": "ri-handbag-line",
    "Belts": "ri-link",
    "Perfumes": "ri-drop-line",
    "Other Stuff": "ri-more-line",
  };

  return (
    <nav
      id="main-nav"
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-transparent"
    >
      <div className="w-full px-4 md:px-6 py-3 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center gap-2 group shrink-0"
        >
          <div className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center">
            <i className="ri-file-excel-2-line text-lg text-white"></i>
          </div>
          <span className="font-heading font-semibold text-lg text-foreground-950 whitespace-nowrap">
            VigorBuy Spreadsheet
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          <Link
            to="/"
            className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-primary-600 rounded-lg hover:bg-primary-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            Home
          </Link>

          <Link
            to="/article/what-is-vigorbuy-spreadsheet"
            className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-primary-600 rounded-lg hover:bg-primary-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            Guides
          </Link>

          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-primary-600 rounded-lg hover:bg-primary-50 transition-colors flex items-center gap-1 whitespace-nowrap cursor-pointer"
            >
              Categories
              <i className={`ri-arrow-down-s-line text-sm transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 rounded-lg py-2 max-h-80 overflow-y-auto border border-background-200/70 z-50">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors whitespace-nowrap"
                    onClick={() => setDropdownOpen(false)}
                  >
                    <div className="w-7 h-7 rounded-md bg-background-200/70 flex items-center justify-center shrink-0">
                      <i className={`${categoryIcons[cat.name] || 'ri-more-line'} text-xs text-foreground-500`}></i>
                    </div>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="px-4 py-2 text-sm font-medium text-accent-700 hover:text-accent-800 rounded-lg hover:bg-accent-50 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5"
          >
            <i className="ri-shopping-bag-3-line text-sm"></i>
            Shopping Guide
          </a>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="ml-2 px-5 py-2 text-sm font-semibold bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Buy It Now
          </a>
        </div>

        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer"
        >
          <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-3 flex flex-col gap-1">
            <Link
              to="/"
              className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-700 hover:text-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                <i className="ri-home-4-line text-primary-600"></i>
              </div>
              Home
            </Link>

            <Link
              to="/article/what-is-vigorbuy-spreadsheet"
              className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-700 hover:text-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
                <i className="ri-book-open-line text-accent-600"></i>
              </div>
              Guides
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-accent-700 hover:text-accent-800 rounded-lg hover:bg-accent-50 transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
                <i className="ri-shopping-bag-3-line text-accent-600"></i>
              </div>
              Shopping Guide
            </a>

            <div className="border-t border-background-200/60 my-2"></div>

            <div className="px-4 py-2">
              <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">Shop by Category</p>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat, idx) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className={`flex items-center gap-2 px-3 py-2.5 text-xs font-medium rounded-lg transition-colors ${
                      idx % 4 === 0 ? 'bg-primary-50 text-primary-700 hover:bg-primary-100' :
                      idx % 4 === 1 ? 'bg-accent-50 text-accent-700 hover:bg-accent-100' :
                      idx % 4 === 2 ? 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200' :
                      'bg-background-200/70 text-foreground-700 hover:bg-background-300/70'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <div className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 ${
                      idx % 4 === 0 ? 'bg-primary-200 text-primary-600' :
                      idx % 4 === 1 ? 'bg-accent-200 text-accent-600' :
                      idx % 4 === 2 ? 'bg-secondary-300 text-secondary-600' :
                      'bg-background-300 text-foreground-500'
                    }`}>
                      <i className={`text-xs ${categoryIcons[cat.name] || 'ri-more-line'}`}></i>
                    </div>
                    <span className="leading-tight">{cat.name}</span>
                  </a>
                ))}
              </div>
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="mx-4 mt-3 mb-2 px-5 py-3 text-sm font-semibold bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-colors text-center cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              Buy It Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}